import { z } from 'zod';
import type { DefaultValues, FieldValues } from 'react-hook-form';
import type { ZodType } from 'zod';

import type { FieldConfig } from '@/types/forms';

export const MARKETING_CONSENT_FIELD_NAME = 'marketingConsent' as const;

export const marketingConsentSchemaShape = {
  [MARKETING_CONSENT_FIELD_NAME]: z.boolean().optional().default(false),
};

export const marketingConsentSchema = z.object(marketingConsentSchemaShape);

export type MarketingConsentValues = z.infer<typeof marketingConsentSchema>;

type MarketingConsentFormConfig = {
  enabled: boolean;
};

export const marketingConsentFormConfig = {
  serviceEnquiry: { enabled: true },
  doctorEnquiry: { enabled: true },
  bloodTestEnquiry: { enabled: true },
  boneScanEnquiry: { enabled: true },
  dentalEnquiry: { enabled: true },
  exroidEnquiry: { enabled: true },
  physiotherapyEnquiry: { enabled: true },
  trtEnquiry: { enabled: true },
  PrivateUltrasoundScansEnquiry: { enabled: true },
  MaleUltrasoundEnquiry: { enabled: true },
  FemaleUltrasoundEnquiry: { enabled: true },
  GPAccessEnquiry: { enabled: true },
  UrgentCareEnquiry: { enabled: true },
  DieteticsEnquiry: { enabled: true },
  ObagiEventEnquiry: { enabled: true },
  skinAnalysisEvent: { enabled: true },
  PaediatricClinicEnquiry: { enabled: true },
  fibroScanEnquiry: { enabled: true },
  investorEnquiry: { enabled: false },
} as const satisfies Record<string, MarketingConsentFormConfig>;

export function isMarketingConsentEnabled(formId: string): boolean {
  const config =
    marketingConsentFormConfig[
      formId as keyof typeof marketingConsentFormConfig
    ];

  return config?.enabled === true;
}

export function withMarketingConsentSchema<T>(
  schema: ZodType<T>,
): ZodType<T & MarketingConsentValues> {
  return z.intersection(schema, marketingConsentSchema) as ZodType<
    T & MarketingConsentValues
  >;
}

export const marketingConsentDefaultValues = {
  [MARKETING_CONSENT_FIELD_NAME]: false,
} as const;

export const marketingConsentField = {
  name: MARKETING_CONSENT_FIELD_NAME,
  type: 'marketing-consent',
  label: 'Marketing consent',
  fullWidth: true,
  required: false,
} satisfies FieldConfig;

export function withMarketingConsentFields(
  fields: FieldConfig[],
): FieldConfig[] {
  if (fields.some((field) => field.name === MARKETING_CONSENT_FIELD_NAME)) {
    return fields;
  }

  return [...fields, marketingConsentField];
}

export function withMarketingConsentDefaultValues<T extends FieldValues>(
  defaultValues: DefaultValues<T>,
): DefaultValues<T & MarketingConsentValues> {
  return {
    ...defaultValues,
    ...marketingConsentDefaultValues,
  } as unknown as DefaultValues<T & MarketingConsentValues>;
}
